import { hooks, state } from "@springtree/eva-sdk-react-recoil";
import { useQuery } from "@tanstack/react-query";
import { Entry } from "contentful";
import { useInView } from "framer-motion";
import { createInfiniteHitsSessionStorageCache } from "instantsearch.js/es/lib/infiniteHitsCache";
import dynamic from "next/dynamic";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteHits, useInstantSearch } from "react-instantsearch";

import { useConfiguration } from "~/contexts/configuration";
import { usePageTracked } from "~/contexts/page-trackings";
import { useUserTokenAlgolia } from "~/hooks/use-user-token-algolia";
import { convertProductFromAgoliaToEva, getDiscountedAndOOSProductsByIds } from "~/services/algolia-utils";
import Algolia from "~/types/algolia";
import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";
import contentfulUtils from "~/utils/contentful-utils";
import TrackingUtils from "~/utils/tracking-utils";

import SkeletonProductTile from "../common/skeletons/skeleton-product-tile";
import CategoryFilters from "./category-filters";
import EditorialElementPLP from "./editorial-element/editorial-element-plp";
import styles from "./product-grid.module.scss";

const ProductTile = dynamic(() => import("./product-tile"), {
  loading: () => <SkeletonProductTile />,
});

type Props = {
  components?: Entry<Editorials.GridElement>[];
  listScope: string;
  categoryId: string;
  totalNumberProducts: number;
  algoliaUserToken: string;
};

export default function AlgoliaProductListing(props: Props) {
  const infiniteHitsSessionStorage = useMemo(() => createInfiniteHitsSessionStorageCache(), []);
  //useUserTokenAlgolia({ token: props.algoliaUserToken, categoryId: props.categoryId });

  const [path, setPath] = useState<string | undefined>(undefined);
  const {
    items: hits,
    isLastPage,
    showMore,
    sendEvent,
    isFirstPage,
    showPrevious,
    results,
  } = useInfiniteHits({ cache: infiniteHitsSessionStorage });
  const { configuration, eva } = useConfiguration();
  const { status } = useInstantSearch();
  const evaEndpointUrl = hooks.useGetState(state.core.evaEndpointUrlState);

  const lastTrackedIndex = useRef<number>(0);
  const isPageTracked = usePageTracked();

  let countEditorialElement = 0;

  useEffect(() => {
    const clearSessionStorage = () => {
      sessionStorage.removeItem("ais.infiniteHits");
    };

    window.addEventListener("pagehide", clearSessionStorage);

    return () => {
      window.removeEventListener("pagehide", clearSessionStorage);
    };
  }, []);

  const productsWithEditorialElement: (Algolia.Product | Entry<Editorials.GridElement>)[] | undefined = useMemo(
    () => contentfulUtils.mergeProductsWithEditorials<Algolia.Product>(hits as Algolia.Product[], props.components),
    [hits, props.components]
  );

  const { data: discountedAndOOSProducts } = useQuery<Algolia.DiscountedAndOOSProducts>({
    queryKey: ["algoliaProductListingAvailabilityAndDiscounts", hits.map((hit) => hit.code)],
    staleTime: Constants.DEFAULT_PLP_STALE_TIME,
    keepPreviousData: true,
    queryFn: async () => {
      return (
        (await getDiscountedAndOOSProductsByIds(
          eva,
          hits.map((hit) => hit.objectID)
        )) ?? {}
      );
    },
    enabled: !!evaEndpointUrl && hits.length > 0,
  });

  useEffect(() => {
    const newPath = window.location.href;

    setPath(newPath);
    if (path && path !== newPath) {
      lastTrackedIndex.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  useEffect(() => {
    if (isPageTracked) {
      if (hits) {
        lastTrackedIndex.current = TrackingUtils.trackAlgoliaProductsListImpression(
          hits as Algolia.Product[],
          lastTrackedIndex.current,
          props.listScope,
          {
            currency: configuration.CurrencyID,
          }
        );
      }
    } else {
      lastTrackedIndex.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageTracked, hits, lastTrackedIndex.current]);

  const sentinelRef = useRef(null);
  const sentinelRef2 = useRef(null);
  const sentinelInView = useInView(sentinelRef, { margin: "0% 0% 50% 0%" });
  const sentinel2InView = useInView(sentinelRef2, { margin: "0% 0% -50% 0%" });

  // handle infinite scroll - show previous page
  useEffect(() => {
    if (sentinel2InView && !isFirstPage) {
      showPrevious();
    }
  }, [isFirstPage, sentinel2InView, showPrevious]);

  // handle infinite scroll
  useEffect(() => {
    if (sentinelInView && !isLastPage) {
      showMore();
    }
  }, [isLastPage, sentinelInView, showMore]);

  const clickEvent = async (algoliaProduct: Algolia.Product) => {
    sendEvent?.("click", algoliaProduct, "Hit Clicked");
    if ("__queryID" in algoliaProduct) {
      // save it in localstorage for cross-tabs navigation and set it as default on session
      localStorage.setItem("algolia_queryid", algoliaProduct.__queryID as string);
      sessionStorage.setItem("algolia_queryid", algoliaProduct.__queryID as string);
    }
  };

  return (
    <>
      <div className={styles.sentinelTop} ref={sentinelRef2} aria-hidden="true" />
      {/* To avoid cumulative layout shift */}
      {hits.length === 0 &&
        status !== "idle" &&
        Array.from({
          length:
            props.totalNumberProducts < Constants.NUMBER_IMAGES_LOADED_WITH_PRIORITY_ON_PLP
              ? props.totalNumberProducts
              : Constants.NUMBER_IMAGES_LOADED_WITH_PRIORITY_ON_PLP,
        }).map((_, i) => (
          <div key={i} className={styles.tile}>
            <SkeletonProductTile />
          </div>
        ))}
      {productsWithEditorialElement?.map((item, idx) => {
        if (contentfulUtils.isContentful(item)) {
          return (
            <EditorialElementPLP
              editorialElement={item}
              key={item.sys.id}
              countEditorialElement={++countEditorialElement}
            />
          );
        } else {
          return (
            <div key={item.objectID} className={styles.tile}>
              <ProductTile
                product={convertProductFromAgoliaToEva(item, configuration.CurrencyID)}
                idxInList={idx}
                listScope={props.listScope}
                noLazy={idx < Constants.NUMBER_IMAGES_LOADED_WITH_PRIORITY_ON_PLP}
                algolia={{ product: item, discountedAndOOSProducts, events: { clickEvent } }}
              />
            </div>
          );
        }
      })}
      <div className={styles.sentinelBottom} ref={sentinelRef} aria-hidden="true" />
      <div className={styles.filters}>
        <CategoryFilters pageType="inPLPAlgolia" />
      </div>
    </>
  );
}
